import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import type { SavedSearchCadence } from '@/types/savedSearch';
import { WyborKadencji } from '@/components/search/WyborKadencji';

/**
 * JEDNORAZOWE PYTANIE O E-MAILE PO PIERWSZYM ZAPISIE (build plan C3, plan E1.5, §1.5, §1.25, §1.34;
 * makiety `serp-saved-filters-proto.html` — okno, `-mobile.html` — arkusz od dołu).
 *
 * Kiedy się otwiera, decyduje `useMojeFiltry` (`pytacOMaile`). Trzy kadencje, „Codziennie”
 * zaznaczone. „Tak, włącz alerty” zapisuje `alerts_enabled` + `cadence`; „Nie, dziękuję” niczego nie
 * zapisuje (powiadomienia są wyłączone). Nieudany zapis zostawia pytanie otwarte z błędem pod
 * przyciskami — żaden komunikat nie leży na pytaniu (§1.25).
 */
export function PytanieOMaile({
  otwarte,
  naTelefonie,
  odpowiedz,
  zamknij,
}: {
  otwarte: boolean;
  naTelefonie: boolean;
  odpowiedz: (kadencja: SavedSearchCadence | null) => Promise<boolean>;
  zamknij: () => void;
}) {
  const { t } = useTranslation();
  const [kadencja, setKadencja] = useState<SavedSearchCadence>('daily');
  const [zapisuje, setZapisuje] = useState(false);
  const [blad, setBlad] = useState(false);
  useEffect(() => {
    if (otwarte) { setKadencja('daily'); setBlad(false); }
  }, [otwarte]);

  const tak = async () => {
    setZapisuje(true);
    setBlad(false);
    const ok = await odpowiedz(kadencja);
    setZapisuje(false);
    if (!ok) setBlad(true);
  };
  const nie = () => { void odpowiedz(null); };
  const naZmianeOtwarcia = (o: boolean) => { if (!o && !zapisuje) zamknij(); };

  const tytul = (
    <>
      <Mail className="mr-2 inline h-5 w-5 align-[-3px]" aria-hidden />
      {t('search.my_filters.mail.title')}
    </>
  );
  const tresc = (
    <>
      <WyborKadencji
        wartosc={kadencja}
        onChange={setKadencja}
        etykieta={t('search.my_filters.mail.title')}
        className="my-3"
      />
      {blad && (
        <p role="alert" data-testid="blad-pytania-o-maile" className="mb-2 text-sm text-destructive">
          {t('search.my_filters.mail.error')}
        </p>
      )}
    </>
  );
  const przyciskTak = (
    <Button key="tak" data-testid="pytanie-tak" onClick={tak} disabled={zapisuje} className={cn(naTelefonie && 'h-11 w-full text-base')}>
      {zapisuje && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
      {t('search.my_filters.mail.yes')}
    </Button>
  );
  const przyciskNie = (
    <Button key="nie" variant="ghost" data-testid="pytanie-nie" onClick={nie} disabled={zapisuje} className={cn(naTelefonie && 'h-11 w-full text-base')}>
      {t('search.my_filters.mail.no')}
    </Button>
  );

  if (naTelefonie) {
    // Telefon: arkusz od dołu, „Tak” nad „Nie” (makieta mobilna).
    return (
      <Sheet open={otwarte} onOpenChange={naZmianeOtwarcia}>
        <SheetContent side="bottom" data-testid="pytanie-o-maile" className="rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))] focus:outline-none" onOpenAutoFocus={(e) => e.preventDefault()}>
          <SheetTitle className="pr-8 text-lg">{tytul}</SheetTitle>
          <SheetDescription className="mt-1.5 text-sm">{t('search.my_filters.mail.body')}</SheetDescription>
          {tresc}
          <div className="grid gap-2">{[przyciskTak, przyciskNie]}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={otwarte} onOpenChange={naZmianeOtwarcia}>
      <DialogContent data-testid="pytanie-o-maile" className="block max-w-[440px] rounded-2xl px-6 py-5" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogTitle className="pr-8 text-lg leading-snug">{tytul}</DialogTitle>
        <DialogDescription className="mt-1.5 text-sm">{t('search.my_filters.mail.body')}</DialogDescription>
        {tresc}
        <div className="flex justify-end gap-2">{[przyciskNie, przyciskTak]}</div>
      </DialogContent>
    </Dialog>
  );
}

export default PytanieOMaile;
